import { ApiResponse } from './api.service';
import { API_URL } from '../config/api.config';
import { apiService } from './api.service';

export interface TenantBranchSummary {
  id: string;
  name: string;
  code: string;
  isMain: boolean;
  isActive: boolean;
}

export interface TenantSummary {
  id: string;
  name: string;
  description?: string;
  logo?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  timezone: string;
  currency: string;
  taxRate: number;
  vatInclusive?: boolean;
  defaultPrintFormat?: 'standard' | 'thermal';
  theme?: 'light' | 'dark';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  branches: TenantBranchSummary[];
  _count?: {
    users: number;
    branches: number;
    products: number;
    orders: number;
    customers?: number;
  };
}

export interface PaginatedTenants {
  data: TenantSummary[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface TenantsQuery {
  page?: number;
  limit?: number;
  search?: string;
}

export interface CreateTenantDto {
  name: string;
  description?: string;
  email?: string;
  adminEmail?: string;
  adminPassword?: string;
  adminFirstName?: string;
  adminLastName?: string;
  phone?: string;
  address?: string;
  website?: string;
  timezone?: string;
  currency?: string;
  taxRate?: number;
  vatInclusive?: boolean;
  defaultPrintFormat?: 'standard' | 'thermal';
  theme?: 'light' | 'dark';
  isActive?: boolean;
  isRestaurant?: boolean;
  orderByTable?: boolean;
}

class TenantsService {
  async getTenants(query?: TenantsQuery): Promise<ApiResponse<PaginatedTenants>> {
    const params = new URLSearchParams();
    if (query?.page) params.append('page', query.page.toString());
    if (query?.limit) params.append('limit', query.limit.toString());
    if (query?.search) params.append('search', query.search);

    const qs = params.toString() ? `?${params.toString()}` : '';
    const token = localStorage.getItem('accessToken');
    const activeTenantId = localStorage.getItem('activeTenantId');
    const currentBranchRaw = localStorage.getItem('currentBranch');
    let activeBranchId: string | null = null;

    if (currentBranchRaw) {
      try {
        const currentBranch = JSON.parse(currentBranchRaw);
        if (currentBranch?.id) {
          activeBranchId = currentBranch.id;
        }
      } catch {
        // Ignore malformed cached branch JSON.
      }
    }

    const res = await fetch(`${API_URL}/tenants${qs}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(activeTenantId ? { 'X-Tenant-Id': activeTenantId } : {}),
        ...(activeBranchId ? { 'X-Branch-Id': activeBranchId } : {}),
      },
    });

    if (!res.ok) {
      throw { message: `Failed to fetch tenants: ${res.status}`, status: res.status };
    }

    const json = await res.json();
    return { success: true, data: json };
  }

  async createTenant(data: CreateTenantDto): Promise<ApiResponse<TenantSummary>> {
    return apiService.post<TenantSummary>('/tenants', data);
  }

  async resetRootAdminPassword(tenantId: string, password: string): Promise<ApiResponse<{ message: string; adminEmail: string }>> {
    return apiService.patch<{ message: string; adminEmail: string }>(`/tenants/${tenantId}/root-admin-password`, { password });
  }

  async deleteTenant(tenantId: string): Promise<ApiResponse<{ message: string }>> {
    return apiService.delete<{ message: string }>(`/tenants/${tenantId}`);
  }
}

export const tenantsService = new TenantsService();